import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createGodown, createInvitation } from "../lib/api";
import type { Godown, Invitation, PortalUser, Report } from "../lib/api";
import {
  DASH,
  districtScopeId,
  fullName,
  godownAgents,
  isPass,
  useDHDistrict,
  useDHGodowns,
  useDHInvitations,
  useDHReports,
  useDHUsers,
} from "./data";
import { Tray } from "../statehead/Tray";

/** Everything one godown row needs, from real data. */
function rowData(
  g: Godown,
  reports: Report[],
  invites: Invitation[],
  users: PortalUser[],
) {
  const rs = reports.filter((r) => r.godown_id === g.id);
  const passed = rs.filter((r) => isPass(r.verdict)).length;
  const ginv = invites.filter(
    (i) =>
      i.role === "godown_qc_agent" &&
      i.target_scope_type === "godown" &&
      i.target_scope_id === g.id,
  );
  const accepted = ginv.find((i) => i.accepted_at);
  const pending = ginv.find((i) => !i.accepted_at && !i.revoked_at);
  const head = accepted || pending || null;
  const agent = godownAgents(users, g.id)[0] ?? null;
  const agentLabel = (agent && fullName(agent)) || head?.email || null;
  const status = accepted ? "live" : pending ? "pending" : "setup";
  return { passed, total: rs.length, agentLabel, status };
}

const STATUS_TAG: Record<string, [string, string]> = {
  live: ["tag-live", "Live"],
  pending: ["tag-setup", "Awaiting confirmation"],
  setup: ["tag-off", "No agent yet"],
};

export function Godowns() {
  const navigate = useNavigate();
  const districtQ = useDHDistrict();
  const godownsQ = useDHGodowns();
  const reportsQ = useDHReports();
  const invitesQ = useDHInvitations();
  const usersQ = useDHUsers();
  const [drawer, setDrawer] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const district = districtQ.data?.district;
  const godowns = godownsQ.data?.godowns ?? [];
  const reports = reportsQ.data?.reports ?? [];
  const invites = invitesQ.data?.invitations ?? [];
  const users = usersQ.data?.users ?? [];

  const say = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 3200);
  };

  return (
    <>
      <p className="crumb">
        {(district?.name ?? "DISTRICT").toUpperCase()} / GODOWNS
      </p>
      <div className="page-head">
        <div>
          <h1>Godowns</h1>
          <p>
            You add godowns and their QC agent. Each agent grades samples on a
            field device — open a godown to see what they've filed.
          </p>
        </div>
        <div className="page-actions">
          <button className="btn btn-gold" onClick={() => setDrawer(true)}>
            Add godown
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="ledger-head">
          <span>GODOWN</span>
          <span>QC AGENT</span>
          <span>PLACE</span>
          <span>REPORTS PASSED / FILED</span>
          <span>STATUS</span>
          <span />
        </div>

        {godownsQ.isLoading && (
          <div style={{ padding: 18 }} className="tray-cap">
            Loading…
          </div>
        )}
        {!godownsQ.isLoading && godowns.length === 0 && (
          <div style={{ padding: 22 }}>
            <div className="empty" style={{ border: 0 }}>
              <b>No godowns yet</b>
              <p>Add your first godown and invite its QC agent.</p>
              <button className="btn btn-gold" onClick={() => setDrawer(true)}>
                Add godown
              </button>
            </div>
          </div>
        )}

        {godowns.map((g) => {
          const r = rowData(g, reports, invites, users);
          const [cls, label] = STATUS_TAG[r.status];
          return (
            <button
              key={g.id}
              className="drow"
              onClick={() => navigate(`/dh/godown/${g.id}`)}
            >
              <span>
                <span className="dname">{g.name}</span>
                <span className="dsub">
                  {(g.place ?? "").trim() || "PLACE NOT SET"}
                </span>
              </span>
              <span className="owner hide-sm">
                {r.agentLabel ?? DASH}
                <span>
                  {r.status === "live"
                    ? "confirmed"
                    : r.status === "pending"
                      ? "invite pending"
                      : "not invited"}
                </span>
              </span>
              <span className="num hide-sm">
                {(g.place ?? "").trim() || DASH}
              </span>
              <span className="hide-sm">
                {r.total > 0 ? (
                  <Tray passed={r.passed} total={r.total} />
                ) : r.status === "pending" ? (
                  <span className="tray-cap" style={{ margin: 0 }}>
                    Grading starts after setup
                  </span>
                ) : (
                  <span className="tray-cap" style={{ margin: 0 }}>
                    {DASH}
                  </span>
                )}
              </span>
              <span className="hide-sm">
                <span className={"tag " + cls}>
                  <span className="dot" />
                  {label}
                </span>
              </span>
              <svg className="chev" viewBox="0 0 16 16" fill="none">
                <path
                  d="M6 3.5L10.5 8 6 12.5"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          );
        })}
      </div>

      <AddGodownDrawer
        open={drawer}
        onClose={() => setDrawer(false)}
        onDone={(msg) => {
          setDrawer(false);
          say(msg);
        }}
      />

      <div className={"toast" + (toast ? " show" : "")}>
        <span className="dot" />
        <span>{toast}</span>
      </div>
    </>
  );
}

function AddGodownDrawer({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [place, setPlace] = useState("");
  const [email, setEmail] = useState("");

  const mut = useMutation({
    mutationFn: async () => {
      const districtId = districtScopeId();
      if (districtId == null) throw new Error("No district on your account.");
      const gres = await createGodown({
        district_id: districtId,
        name: name.trim(),
        place: place.trim() || undefined,
      });
      const godownId = gres.godown.id;
      await createInvitation({
        email: email.trim(),
        role: "godown_qc_agent",
        target_scope_type: "godown",
        target_scope_id: godownId,
      });
      return name.trim();
    },
    onSuccess: (savedName) => {
      qc.invalidateQueries({ queryKey: ["sh-godowns"] });
      qc.invalidateQueries({ queryKey: ["sh-invitations"] });
      setName("");
      setPlace("");
      setEmail("");
      onDone(`${savedName} added. Invite sent to the QC agent.`);
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || mut.isPending) return;
    mut.mutate();
  };

  return (
    <>
      <div
        className={"scrim" + (open ? " open" : "")}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={"drawer" + (open ? " open" : "")}
        role="dialog"
        aria-modal="true"
      >
        <div className="drawer-head">
          <div>
            <h2>Add a godown</h2>
            <p>
              You add the godown and its QC agent. They grade samples on a
              field device once they confirm their profile.
            </p>
          </div>
          <button className="x" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <form onSubmit={onSubmit} style={{ display: "contents" }}>
          <div className="drawer-body">
            <span className="step">GODOWN</span>
            <div className="grid-2">
              <div className="field">
                <label>Godown name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Sonbarsa Godown"
                  autoFocus
                />
              </div>
              <div className="field">
                <label>
                  Place<span className="hint">optional</span>
                </label>
                <input
                  value={place}
                  onChange={(e) => setPlace(e.target.value)}
                  placeholder="Sonbarsa"
                />
              </div>
            </div>
            <span className="step">QC AGENT</span>
            <div className="field">
              <label>
                Work email
                <span className="hint">the invite goes here</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
              />
            </div>
            <div className="notice">
              <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M2 4.5h12v8H2v-8z" stroke="currentColor" strokeWidth="1.4" />
                <path d="M2 5l6 4 6-4" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
              </svg>
              <p>
                <b>What happens next</b>
                AgriLens emails the QC agent an invite to set a password and
                join. Until they accept, the godown shows as awaiting
                confirmation and has no reports.
              </p>
            </div>
            {mut.isError && (
              <div
                className="notice"
                style={{ background: "#FDE8E8", borderColor: "#F5B5B5" }}
              >
                <p style={{ color: "#B00020" }}>
                  {(mut.error as Error).message}
                </p>
              </div>
            )}
          </div>
          <div className="drawer-foot">
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-gold"
              disabled={mut.isPending}
            >
              {mut.isPending ? "Saving…" : "Save and send invite"}
            </button>
          </div>
        </form>
      </aside>
    </>
  );
}
